import { mock } from "./mockData.js";
import apiLink from "../../../../config.js";
import { deconnecterSiTokenInvalide } from "./session.js";

// 👉 Un flag par endpoint : passe chacun à `false` au fur et à mesure que
// les routes de Clément sont prêtes, plutôt que tout basculer d'un coup.
// Tant qu'un flag est à `true`, cet endpoint tourne sur mockData.js.
export const USE_MOCK = {
  // idExploitation n'est pas encore pris en compte par l'API (en cours côté
  // Clément) : ces endpoints tournent sur mock en attendant.
  impactsGlobaux: false,
  pheno: false,
  aleas: false,
  pertes: false,
  eau: false,
  eauElevage: false,
  risqueActuel: false,
  carteRisqueActuel: false,
  marge: false,
  cartesPhenologie: true,
  cartesFrequenceAleas: true,
  cartesEau: true,
  carteRisquePRA: true, // toutes les cartes en mock pour l'instant
  elevage: true,        // graphique de l'elevage dans tableau de bord
  tension: false,       // en test avec la vraie route (apiPlumberFetch "tension")
  classement: false,    // route branchée sur l'API (apiPlumberFetch "classement")
  semisVariete: true,   // nouvelle route à créer côté API
  substitution: true,   // nouvelle route à créer côté API
};

const MOCK_TRACC_UNTIL_API_READY = false;

const BASE_URL = "https://api2050.diagorisk.com";

/**
 * L'OAD tape sur deux bases distinctes, avec chacune sa propre fonction (pas
 * de fonction générique unique) :
 *  - apiOriginFetch  -> apiLink (origin/api/1.0), les routes historiques de
 *    l'appli (page/optimisation, getLocalisation, getCommune, page/exploitation...) ;
 *  - apiPlumberFetch -> BASE_URL (api2050.diagorisk.com), les routes issues
 *    de DiagoEau / du calcul RPlumber (eau, pertes, marge, resultatExploitation...).
 * Même contrat par ailleurs : POST + Bearer + réponse JSON, erreur si
 * `{ error }` dans la réponse.
 */
async function apiOriginFetch(endpoint, body) {
  const token = localStorage.getItem("token");

  const response = await fetch(apiLink + endpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(body),
  });

  // Token invalide / expiré : déconnexion et retour à l'accueil.
  deconnecterSiTokenInvalide(response);

  if (!response.ok) {
    throw new Error(`Erreur API ${endpoint} (${response.status})`);
  }

  const data = await response.json();

  if (data && typeof data === "object" && "error" in data) {
    throw new Error(data.error);
  }

  return data;
}

async function apiPlumberFetch(endpoint, body) {
  const token = localStorage.getItem("token");

  const response = await fetch(`${BASE_URL}/${endpoint}/`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(body),
  });

  // Token invalide / expiré : déconnexion et retour à l'accueil.
  deconnecterSiTokenInvalide(response);

  if (!response.ok) {
    throw new Error(`Erreur API ${endpoint} (${response.status})`);
  }

  const data = await response.json();

  if (data && typeof data === "object" && "error" in data) {
    throw new Error(data.error);
  }

  return data;
}

/**
 * Reconstitue exactement la logique de `resultats2030_2050.js` (l'ancienne
 * page 2030-2050), qui tape sur 3 routes différentes pour Impacts globaux :
 *
 *  1. apiOriginFetch("page/optimisation")   -> facteurs de correction
 *     (MSF/COSF) + la série "sans assurance" affichée en comparaison sur
 *     GraphDifference (dataGraph2020SimFerme).
 *  2. apiPlumberFetch("resultatExploitation") -> la vraie donnée : assolement
 *     (dataSurface/dataPB) et les risques par horizon/scénario
 *     (R<annee>_<scenario>, RMoy_<scenario>).
 *  3. apiOriginFetch("getLocalisation") puis apiOriginFetch("getCommune")
 *     (chaînés, le 2e a besoin de l'idCommune renvoyé par le 1er) -> commune
 *     et petite région agricole affichées sous le titre des tableaux.
 *
 * Ce ne sont pas des variables qu'on choisit d'envoyer : ce sont des valeurs
 * reçues de l'API elle-même, donc pas de champ à ajouter à `params`.
 */
const ANNEES_IMPACTS = [2020, 2025, 2030, 2035, 2040, 2045, 2050];

async function fetchCorrectionOrigin(params) {
  const reponse = await apiOriginFetch("page/optimisation", params);
  const MSF = Number(
    parseFloat(reponse.dataGraphCN["Sans Assurance"][1] * 100).toFixed(1)
  );
  const COSF = Number(parseFloat(reponse.risques7).toFixed(1));
  return {
    MSF,
    COSF,
    // 1er élément volontairement vide : la série ne compte que 4 points,
    // alignés sur les 4 niveaux de risque affichés (Très courant excepté).
    dataGraph2020SimFerme: [undefined, reponse.risques7, reponse.risques20, reponse.risques50],
  };
}

async function fetchLocalisationOrigin(params) {
  const localisation = await apiOriginFetch("getLocalisation", params);
  const idCommune = parseInt(localisation.idCommune, 10);
  const commune = await apiOriginFetch("getCommune", { idCommune });
  return {
    commune: `${commune.codePostal} - ${commune.libelleCommune}`,
    libellePRA: commune.libellePetiteRegionAgricole,
  };
}

async function fetchImpactsGlobauxReel(params) {
  const [{ MSF, COSF, dataGraph2020SimFerme }, brut, localisation] = await Promise.all([
    fetchCorrectionOrigin(params),
    apiPlumberFetch("resultatExploitation", { idExploit: params.idExploitation }),
    fetchLocalisationOrigin(params),
  ]);

  // RefCo/difCo/difMoy : mêmes formules que l'ancienne page, à l'identique.
  const RefCo = brut.R2020_2degre[1];
  const corriger = RefCo > COSF;
  const difCo = 1.25 * (RefCo - COSF);
  const difMoy = brut.RMoy_2degre[0] - MSF;

  const corrigerSerie = (valeurs) =>
    corriger ? valeurs.map((v) => v - difCo) : valeurs;

  const resultat = { ...brut, ...localisation, dataGraph2020SimFerme };

  ["2degre", "4degre"].forEach((scenario) => {
    ANNEES_IMPACTS.forEach((annee) => {
      const champ = `R${annee}_${scenario}`;
      if (Array.isArray(brut[champ])) {
        resultat[champ] = corrigerSerie(brut[champ]);
      }
    });
  });

  // RMoy_2degre : décalage simple par rapport à MSF, toujours appliqué.
  resultat.RMoy_2degre = brut.RMoy_2degre.map((v) => v - difMoy);
  // RMoy_4degre : décalage auto-référencé (recalé sur son propre indice 0),
  // formule différente de RMoy_2degre mais reprise telle quelle de l'origine.
  const decalage4degre = brut.RMoy_4degre[0] - MSF;
  resultat.RMoy_4degre = brut.RMoy_4degre.map((v) => v - decalage4degre);

  resultat.PBTot = (brut.dataPB ?? []).reduce((somme, item) => somme + item.value, 0);

  return resultat;
}

/**
 * Contrat API attendu côté RPlumber (voir README.md pour le détail complet).
 * Toute la logique métier (blend hybride RCP4.5/8.5, lissage, calculs de marge,
 * actualisation, etc.) reste côté R — le front ne fait qu'afficher le JSON reçu.
 *
 * ⚠️ Les noms d'endpoints ci-dessous ("config", "eau", "irrigation", ...) sont
 * ceux du contrat qu'on avait posé — à aligner avec les vrais noms de routes
 * une fois côté Clément (ex: peut-être "config2050", "eauJournalier", etc.).
 */
/**
 * Chaîne complète pour la carte de Comparer / Risque actuel (voir le
 * commentaire sur getCarteRisqueActuel ci-dessous pour le détail des 5
 * routes). idTypeExploitationType : 1 = grandes cultures pures,
 * 2 = mixte viticulture — détecté depuis l'assolement, pas choisi par
 * l'utilisateur.
 */
const ROUTES_GEO_PAR_ECHELLE = {
  pra: { route: "getGeoPetiteRegionAgricole", idBody: "idPetiteRegionAgricole", idLocalisation: "idPetiteRegionAgricole" },
  ra: { route: "getGeoRegionAgricole", idBody: "idRegionAgricole", idLocalisation: "idRegionAgricole" },
  departement: { route: "getGeoDepartement", idBody: "idDepartement", idLocalisation: "departement" },
  region: { route: "getGeoRegion", idBody: "idRegion", idLocalisation: "idRegion" },
};

async function fetchCarteRisqueActuelReel({ idExploitation, echelle = "pra" }) {
  const [localisation, assolement, pointBrut] = await Promise.all([
    apiOriginFetch("getLocalisation", { idExploitation }),
    apiOriginFetch("page/production-rapport", { idExploitation }),
    apiOriginFetch("getGeoLocaExploitation", { idExploitation }),
  ]);

  const viticulture = Object.keys(assolement ?? {}).some((cle) => cle.includes("Vignes"));
  const idTypeExploitationType = viticulture ? 2 : 1;

  const { route, idBody, idLocalisation } = ROUTES_GEO_PAR_ECHELLE[echelle] ?? ROUTES_GEO_PAR_ECHELLE.pra;
  const idZone = localisation[idLocalisation];

  const geo = await apiOriginFetch(route, { [idBody]: idZone, idTypeExploitationType });

  const pointFeature = pointBrut?.data?.features?.[0] ?? pointBrut?.features?.[0];
  const pointExploitation = pointFeature
    ? { lat: pointFeature.geometry.coordinates[1], lng: pointFeature.geometry.coordinates[0] }
    : undefined;

  return {
    carte: { data: geo.data },
    pointExploitation,
    viticulture,
  };
}

export const api = {
  /**
   * Tableau de bord — assolement et produit brut.
   * Réponse : { dataSurface, dataPB, surfaceTotale, PBTotal, libelleRA }
   */
  // Route existante en production : pas de variante mock.
  getExploitation: (params) => apiOriginFetch("page/exploitation", params),

  /**
   * Impacts globaux — risques par horizon et par scénario.
   * Renvoie les champs à plat R<horizon>_<scenario> + RMoy_<scenario>,
   * normalisés côté front par utils/normaliserImpacts.js.
   * Combine 3 routes réelles (voir fetchImpactsGlobauxReel plus haut) : ce
   * n'est PAS un simple apiOriginFetch("page/optimisation", ...).
   */
  getImpactsGlobaux: (params) =>
    USE_MOCK.impactsGlobaux
      ? mock.getImpactsGlobaux(params)
      : fetchImpactsGlobauxReel(params),

  /**
   * Tableau de bord — effectifs du cheptel (image 492).
   * Contrat proposé : { idExploitation } -> { categories: [{ categorie, effectif }] }
   */
  /**
   * Eau et irrigation / Tension — pertes par élément suivi.
   * Contrat proposé : { idExploitation, annee, rcp, elements } -> { elements: [...] }
   */
  getTension: (params) =>
    USE_MOCK.tension
      ? mock.getTension(params)
      : apiPlumberFetch("tension", params),

  /** Adaptations / Classement des solutions. */
  getClassementSolutions: (params) =>
    USE_MOCK.classement
      ? mock.getClassementSolutions(params)
      : apiPlumberFetch("classement", params),

  /** Adaptations / Changement de date de semis ou de variété. */
  getChangementSemisVariete: (params) =>
    USE_MOCK.semisVariete
      ? mock.getChangementSemisVariete(params)
      : apiOriginFetch("page/adaptations/semis-variete", params),

  /** Adaptations / Substitution de culture. */
  getSubstitution: (params) =>
    USE_MOCK.substitution
      ? mock.getSubstitution(params)
      : apiOriginFetch("page/adaptations/substitution", params),

  /**
   * Assolement de l'exploitation : { "<libellé culture>": [surface, rendement,
   * prix, produit brut] }. Route existante, pas de variante mock.
   * Utilisée par les pages Adaptations pour proposer les cultures réellement
   * présentes chez l'utilisateur.
   */
  getProductionRapport: (params) =>
    apiOriginFetch("page/production-rapport", params),

  /** Impacts globaux — carte du risque moyen de la PRA et de ses voisines. */
  getCarteRisquePRA: (params) =>
    USE_MOCK.carteRisquePRA
      ? mock.getCarteRisquePRA(params)
      : apiOriginFetch("page/carte-risque-pra", params),

  /**
   * Comparer / Risque actuel — graphique 3 vagues. Contrat réel confirmé :
   * { idExploitation } -> { dataVagues7, dataVagues20, dataVagues50,
   * dataPerso, libelleExploitation1 }, une forme plate (voir mockData.js).
   */
  getRisqueActuel: (params) =>
    USE_MOCK.risqueActuel
      ? mock.getRisqueActuel(params)
      : apiOriginFetch("page/risquerendement", { idExploitation: params.idExploitation }),

  /**
   * Comparer / Risque actuel — la carte. Contrat RÉEL confirmé, retrouvé
   * dans l'ancien risquesRDT.js (5 routes chaînées, ce n'est PAS une route
   * unique) :
   *  1. getLocalisation({idExploitation}) -> idPetiteRegionAgricole /
   *     idRegionAgricole / departement / idRegion de l'exploitation.
   *  2. page/production-rapport({idExploitation}) -> sert uniquement à
   *     détecter la présence de vigne ("Vignes" dans une clé) : ça bascule
   *     idTypeExploitationType à 2 (mixte viti) au lieu de 1 (grandes
   *     cultures pures) — la base de comparaison n'est pas la même.
   *  3. Une route par échelle ({idZone, idTypeExploitationType}) :
   *     getGeoPetiteRegionAgricole / getGeoRegionAgricole / getGeoDepartement
   *     / getGeoRegion -> { data: FeatureCollection } avec risques7/20/50
   *     déjà à la bonne échelle (pas de /10 à faire, contrairement au mock).
   *  4. getGeoLocaExploitation({idExploitation}) -> FeatureCollection à un
   *     point (siège social), affiché comme un cercle de 1500 m (pas un
   *     marqueur en pixels).
   * Le niveau de risque (Courant/Exceptionnel/Catastrophique) ne fait PAS
   * partie de la requête : les 3 champs arrivent d'un coup, c'est le
   * composant qui choisit lequel afficher.
   */
  getCarteRisqueActuel: (params) =>
    USE_MOCK.carteRisqueActuel
      ? mock.getCarteRisqueActuel(params)
      : fetchCarteRisqueActuelReel(params),

  /** Comparer / Phénologie : 2 cartes (2035/2050), tons bleus. */
  getCartesPhenologie: (params) =>
    USE_MOCK.cartesPhenologie
      ? mock.getCartesPhenologie(params)
      : apiOriginFetch("page/comparer-phenologie", params),

  /** Comparer / Fréquence des aléas : 3 cartes, teinte selon l'aléa choisi. */
  getCartesFrequenceAleas: (params) =>
    USE_MOCK.cartesFrequenceAleas
      ? mock.getCartesFrequenceAleas(params)
      : apiOriginFetch("page/comparer-frequence-aleas", params),

  /** Comparer / Eau : 2 cartes (2035/2050), dégradé rouge/bleu. */
  getCartesEau: (params) =>
    USE_MOCK.cartesEau
      ? mock.getCartesEau(params)
      : apiOriginFetch("page/comparer-eau", params),

  getElevage: (params) =>
    USE_MOCK.elevage
      ? mock.getElevage(params)
      : apiOriginFetch("page/elevage", params),


  getEauIrrigation: (params) =>
    // Le volet élevage bascule indépendamment du volet cultures : tant que
    // l'API ne couvre pas l'élevage, seules ces requêtes-là partent au mock.
    (params?.type === "elevage" ? USE_MOCK.eauElevage : USE_MOCK.eau)
      ? mock.getEauIrrigation(params)
      : MOCK_TRACC_UNTIL_API_READY && params.rcp === "tracc"
      ? mock.getEauIrrigationTRACC(params)
      : apiPlumberFetch("eau", params),

  getPheno: (params) =>
    USE_MOCK.pheno
      ? mock.getPheno(params)
      : MOCK_TRACC_UNTIL_API_READY && params.rcp === "tracc"
      ? mock.getPhenoTRACC(params)
      : apiPlumberFetch("pheno", params),

  getAleas: (params) =>
    USE_MOCK.aleas
      ? mock.getAleas(params)
      : MOCK_TRACC_UNTIL_API_READY && params.rcp === "tracc"
      ? mock.getAleasTRACC(params)
      : apiPlumberFetch("aleas", params),

  getPertes: (params) =>
    // En mock aussi, TRACC a son propre jeu de données : ses groupes sont des
    // niveaux de réchauffement ("reference", "+2", "+2.7") et non des années.
    // Sans cette branche, le camembert restait vide en TRACC.
    // ⚠️ Contrat réel confirmé : { idExploitation, rcp, annee } -> une seule
    // réponse consolidée { aleasParCulture, repartitionCultures,
    // repartitionAleas } — ce n'est PAS 3 routes séparées comme codé au
    // départ. Voir shared/utils/pertes.js pour l'extraction par horizon.
    USE_MOCK.pertes
      ? params.rcp === "tracc"
        ? mock.getPertesTRACC(params)
        : mock.getPertes(params)
      : apiPlumberFetch("pertes", {
          idExploitation: params.idExploitation,
          rcp: params.rcp,
          annee: params.annee,
        }),

  getMarge: (params) =>
    USE_MOCK.marge
      ? mock.getMarge(params)
      : apiPlumberFetch("marge", params),


  // Export PPTX : cas particulier, la réponse n'est pas du JSON mais un blob binaire
};
